'use client';

import { c as _c } from "react/compiler-runtime";
import type { listLocationsForOrganizationAction } from '@/actions/locations';
import { Building2Icon, ChevronRightIcon, ChevronsUpDown, MapPinIcon, Plus, StarIcon } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { useLocations } from '@/hooks/use-locations';
import { formatRating, fullAddress } from '@/lib/utils';
import Link from 'next/link';
import { useState } from 'react';
type Location = Awaited<ReturnType<typeof listLocationsForOrganizationAction>>[number];
export function LocationSwitcher() {
  const $ = _c(32);
  const {
    isMobile
  } = useSidebar();
  const {
    data: locations,
    isLoading,
    activeLocation,
    setActiveLocation
  } = useLocations();
  const [open, setOpen] = useState(false);
  const hasLocations = Boolean(locations?.length);
  let t0;
  if ($[0] !== activeLocation || $[1] !== locations) {
    t0 = activeLocation ?? locations?.at(0);
    $[0] = activeLocation;
    $[1] = locations;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const selectedLocation = t0;
  if (isLoading || !locations) {
    let t1;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Skeleton className="w-full h-12 group-data-[collapsible=icon]:h-8" />;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="p-1.5 bg-linear-to-br from-blue-500/10 to-blue-600/10 rounded-lg group-data-[collapsible=icon]:ml-0.5"><Building2Icon className="size-4 text-blue-500" /></div>;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = selectedLocation?.name ?? "No location yet";
  let t3;
  if ($[5] !== t2) {
    t3 = <span className="truncate font-medium">{t2}</span>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== selectedLocation) {
    t4 = selectedLocation ? <span className="flex items-center gap-1"><MapPinIcon className="size-3 min-w-3 text-muted-foreground" /><span className="truncate text-xs">{fullAddress(selectedLocation.address)}</span></span> : <span className="truncate text-xs text-muted-foreground">Add a location to get started</span>;
    $[7] = selectedLocation;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <div className="flex flex-col justify-start text-sm leading-tight truncate">{t3}{t4}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ChevronsUpDown className="ml-auto" />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5) {
    t7 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" className="flex items-center data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground border border-gray-200/50 group-data-[collapsible=icon]:border-none transition-colors">{t1}{t5}{t6}</SidebarMenuButton></DropdownMenuTrigger>;
    $[13] = t5;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = isMobile ? "bottom" : "right";
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="text-muted-foreground flex items-center gap-2">Locations</span>;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setOpen(false);
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <DropdownMenuLabel className="text-xs flex items-center justify-between gap-2">{t9}<Link href="/app/settings/locations" className="text-primary font-medium flex items-center gap-0.5" onClick={t10}>Manage<ChevronRightIcon className="size-3 translate-y-0.25" /></Link></DropdownMenuLabel>;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== hasLocations || $[19] !== locations || $[20] !== setActiveLocation) {
    t12 = hasLocations ? locations.map(location => <DropdownMenuItem key={location.name} onClick={() => setActiveLocation(location as Location)} className="flex items-center gap-3 flex-1 text-left"><div className="p-1.5 bg-linear-to-br from-blue-500/10 to-blue-600/10 rounded-lg"><Building2Icon className="size-3 text-blue-500" /></div><div className="grid flex-1 text-left text-sm leading-tight"><span className="font-medium text-xs truncate">{location.name}</span><span className="text-xs flex items-center gap-1 text-muted-foreground">{location.reviews && location.reviews > 0 && location.rating ? <><StarIcon className="size-2.5 shrink-0 text-yellow-400" fill="currentColor" />{" "}<span>{formatRating(location.rating)}</span><span>•</span><span>{location.reviews} reviews</span></> : <span className="text-primary font-medium flex items-center gap-1"><StarIcon className="size-2.5 shrink-0 text-yellow-400" fill="currentColor" />{" "}Get your first review</span>}</span></div></DropdownMenuItem>) : <div className="px-2 py-3 text-xs text-muted-foreground">No locations yet.</div>;
    $[18] = hasLocations;
    $[19] = locations;
    $[20] = setActiveLocation;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <DropdownMenuSeparator />;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setOpen(false);
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <DropdownMenuItem asChild={true}><Link href="/app/settings/locations" className="gap-2 p-2" onClick={t14}><div className="flex size-6 items-center justify-center rounded-lg bg-linear-to-br from-blue-500/10 to-blue-600/10"><Plus className="size-4 text-blue-500" /></div><div className="font-medium text-xs">Add a location</div></Link></DropdownMenuItem>;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t12 || $[26] !== t8) {
    t16 = <DropdownMenuContent className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg" align="start" side={t8} sideOffset={4}>{t11}{t12}{t13}{t15}</DropdownMenuContent>;
    $[25] = t12;
    $[26] = t8;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== open || $[29] !== t16 || $[30] !== t7) {
    t17 = <SidebarMenu><SidebarMenuItem><DropdownMenu open={open} onOpenChange={setOpen}>{t7}{t16}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[28] = open;
    $[29] = t16;
    $[30] = t7;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  return t17;
}